import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { connect } from 'react-redux'
import { compose } from 'recompose'

import { Modal } from 'containers/misc'
import { Select } from 'components/fields'
import { Button, FormData, Module, ModuleLink } from 'components/misc'
import { apiAwards, apiCommon, apiLeaders, apiOrganizations } from 'helpers/decorators/api'
import { form, permissions } from 'helpers/decorators'
import { modal } from 'redux/modules/app'
import { organizationsCodeListDropdownSelector } from 'redux/selectors/organizations'
import { leadersDropdownSelector } from 'redux/selectors/leaders'
import activeAward from 'helpers/activeAward'
import { validateAuChange } from 'models/participants'
import { ORGANIZATION_HIERARCHY, ROLES } from 'constants/enums'

import { getActiveRole } from 'utils/token'
import appConfig from 'config'

const enhance = compose(
  apiCommon(),
  permissions({
    notFor: ['LEADER']
  }),
  apiOrganizations({
    filter: 'awardUnits'
  }),
  apiLeaders({
    filter: 'awardUnitLeaders',
    list: true,
    disableQueryFilter: true
  }),
  apiAwards({
    updateParticipantDetail: true
  }),
  connect(
    state => ({
      dropdownOrganizations: organizationsCodeListDropdownSelector(state),
      dropdownLeaders: leadersDropdownSelector(state)
    }),
    {
      modal
    }
  ),
  form({
    form: 'form-participant-award-unit',
    validate: validateAuChange
  })
)

class ParticipantAwardUnitChange extends Component {
  static propTypes = {
    dropdownOrganizations: PropTypes.array.isRequired,
    submitting: PropTypes.bool,
    dropdownLeaders: PropTypes.array.isRequired,
    modal: PropTypes.func.isRequired,
    fetchCountriesWithStructure: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    changeLeader: PropTypes.func.isRequired,
    refreshAward: PropTypes.func,
    getOrganizationCodeList: PropTypes.func.isRequired,
    getLeaders: PropTypes.func.isRequired,
    data: PropTypes.object,
    organizations: PropTypes.object,
    activeOrganization: PropTypes.object,
    leaders: PropTypes.object,
    common: PropTypes.object,
    values: PropTypes.object,
    fieldPrefix: PropTypes.string,
    reset: PropTypes.func
  }

  static defaultProps = {
    values: {}
  }

  getPrefix = () =>
    (typeof this.props.fieldPrefix === 'string'
      ? this.props.fieldPrefix
      : `awards[${activeAward(this.props.data)}].`)

  handleModalOpen = () => {
    if (this.isSuperUser) {
      const { activeOrganization } = this.props

      let filterCase = ''

      let filterId = ''

      switch (getActiveRole(activeOrganization)) {
        case ROLES.IF_REGION.u:
          filterCase = 'regionCountries'
          filterId = activeOrganization.regionId
          break

        default:
          break
      }

      this.props.fetchCountriesWithStructure({ filterCase, filterId })

      this.props.modal('participant-change-award-unit')
      return
    }

    this.props.getOrganizationCodeList()
    this.props.modal('participant-change-award-unit')
  }

  isSuperUser = getActiveRole(this.props.activeOrganization) ===
    ROLES.IF_STAFF_SUPER.u ||
    getActiveRole(this.props.activeOrganization) === ROLES.GBT.u ||
    getActiveRole(this.props.activeOrganization) === ROLES.OA.u ||
    getActiveRole(this.props.activeOrganization) === ROLES.SOA.u ||
    getActiveRole(this.props.activeOrganization) === ROLES.IF_REGION.u

  handleFetchAwardUnits = (obj = {}) =>
    this.props.getOrganizationCodeList({
      filterById: obj.value,
      filterCase: 'countryAwardUnitsOrIacs'
    })

  handleAuSelect = ({ value }) =>
    this.props.getLeaders(value, { onlyActive: true })

  handleSubmit = fields =>
    this.props
      .changeLeader(
        get(this.props.data, `${this.getPrefix()}id`),
        fields.leaderId,
        'AWARD_LEADER'
      )
      .then(() => {
        setTimeout(this.props.reset, 200)
        setTimeout(this.props.refreshAward, 200)
      })

  render () {
    const {
      data,
      common,
      dropdownOrganizations,
      dropdownLeaders,
      values,
      handleSubmit,
      organizations,
      leaders,
      submitting
    } = this.props

    const prefix = this.getPrefix()
    const organizationHierarchyLevel = get(
      data,
      `${prefix}organization.organizationHierarchyLevel.id`
    )

    let countriesList = common.countries.list

    switch (getActiveRole(this.props.activeOrganization)) {
      case ROLES.GBT.u:
        countriesList = common.ics.list
        break
      default:
        break
    }

    const buttonDefinition = [
      {
        'order': 1,
        'secondary': true,
        'onClick': this.handleModalOpen,
        'iconAction': null,
        'isActionLoading': false,
        'intlKeyAction': 'button.change',
        'action': 'Change'
      }
    ]

    return (
      <div>
        <Module
          title="Award unit"
          intlKey="moduleTitle.award_unit"
          primary
          grid
          buttonDefinition={buttonDefinition}
        >
          <div className="row">
            <div className="col-xs-6">
              <FormData
                label="Name of the unit"
                intlKey="label.name_of_the_unit"
              >
                <ModuleLink to={`${appConfig.publicPath}/award-units/${get(data, `${prefix}organization.id`)}/detail`}
                            text={get(data, `${prefix}organization.name`)} />
              </FormData>
            </div>
            <div className="col-xs-6">
              <FormData label="Country" intlKey="label.country">
                {get(data, `${prefix}organization.country.name`)}
              </FormData>
            </div>
          </div>
        </Module>
        <Modal id="participant-change-award-unit" enableOverflow customActions>
          <form onSubmit={handleSubmit(this.handleSubmit)}>

            {this.isSuperUser &&
              <div className="row">
                <div className="col-xs-12">
                  <Select
                    label="Country"
                    name="countryId"
                    intlKey="label.country"
                    onSelectChange={this.handleFetchAwardUnits}
                    options={countriesList}
                    loading={common.loading}
                    searchable
                  />
                </div>
              </div>}

            <div className="row">
              <div className="col-xs-6">
                <Select
                  label="Award unit"
                  name="awardUnitId"
                  intlKey={
                    organizationHierarchyLevel === ORGANIZATION_HIERARCHY.IAC.id
                      ? 'label.independent_award_centre'
                      : 'label.award_unit'
                  }
                  options={dropdownOrganizations}
                  onSelectChange={this.handleAuSelect}
                  searchable
                  disabled={this.isSuperUser && !values.countryId}
                  loading={organizations.loading}
                />
              </div>
              <div className="col-xs-6">
                <Select
                  label="Leaders"
                  name="leaderId"
                  intlKey="label.leaders"
                  options={dropdownLeaders}
                  disabled={!get(values, 'awardUnitId')}
                  searchable
                  loading={leaders.loading}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-xs-6">
                <Button
                  secondary
                  type="button"
                  intlKey="button.close"
                  onClick={() => this.props.modal(null)}
                >
                  Close
                </Button>
              </div>
              <div className="col-xs-6 text--right">
                <Button
                  intlKey="button.change"
                  isLoading={submitting}
                  disabled={!get(values, 'leaderId')}
                  onClick={() => this.props.modal(null)}
                >
                  Change
                </Button>
              </div>
            </div>
          </form>
        </Modal>
      </div>
    )
  }
}

export default enhance(ParticipantAwardUnitChange)
